import type { LibraryReport } from "@/hooks/use-library-report";

// Pure decision helpers for "the library (`drugs`) is the SINGLE source of truth
// for what a clinician sees". Kept out of the component so they can be
// unit-tested without mocking Firestore.
//
// A `drug_searches` doc carries only a pointer (drug_id + report_version), never
// a copy of the report — so there is no second source to pick between at render
// time. What remains is a tri-state ("is the library report here yet, or is it
// genuinely absent?") plus the reattach decision.

// Whether a completed `drug_searches` doc may be reattached to instead of
// scheduling a fresh search. Reuse requires the library to actually hold a
// report for the served drug: `latestVersion` is `drugs/{drug_id}.latest_version`.
//
// An unknown / absent / zero version means the drug is NOT in the library
// (purged, never re-seeded, or the pointer is dangling) — refusing reuse there
// is what makes such a query a cache miss that regenerates, instead of a search
// that reattaches and paints a report the backoffice library no longer lists.
export function canReuseCompletedSearch(
  latestVersion: number | null | undefined,
): boolean {
  return latestVersion != null && latestVersion >= 1;
}

// The backend function is killed at 540 s, so a pending/processing doc older
// than this never finishes — reattaching to it strands the doctor on a timer
// until they refresh.
export const REATTACH_ACTIVE_MAX_AGE_MS = 10 * 60 * 1000;

export interface ReusableSearchRow {
  id: string;
  status?: string;
  // ISO timestamp set by the client on create.
  created_at?: string;
  results?: { drug_id?: string };
}

function isFreshActive(row: ReusableSearchRow, nowMs: number): boolean {
  if (row.status !== "pending" && row.status !== "processing") return false;
  const createdMs = row.created_at ? new Date(row.created_at).getTime() : NaN;
  if (!Number.isFinite(createdMs)) return false;
  return nowMs - createdMs < REATTACH_ACTIVE_MAX_AGE_MS;
}

// Choose which existing `drug_searches` row for the same user + query to
// reattach to: a fresh in-flight row, else the newest completed row, else
// null (start a new search). A stale in-flight row — older than the backend
// could still be running, or without a parseable `created_at` — is ignored.
// The caller still has to confirm a completed pick against the library.
export function pickReusableSearch(
  rows: ReusableSearchRow[],
  nowMs: number,
): ReusableSearchRow | null {
  const active = rows.find((row) => isFreshActive(row, nowMs));
  if (active) return active;
  const newestCompleted = rows
    .filter((row) => row.status === "completed")
    .sort((a, b) =>
      String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")),
    )[0];
  return newestCompleted ?? null;
}

export type ReportState =
  // No drug to resolve — nothing is being shown.
  | "idle"
  // A drug id is being resolved; the library listener has not settled.
  | "loading"
  // The library report is here and renderable.
  | "ready"
  // Settled: the library holds no report for this drug id.
  | "missing";

export interface ReportStateInput {
  // The id whose library report we are resolving; null when there is none.
  drugId: string | null | undefined;
  libraryReport: LibraryReport | null | undefined;
  loading: boolean;
  // `useLibraryReport`'s settled flag: null = not settled yet.
  found: boolean | null;
}

// Classify what the report area should show. "loading" until the library
// listener settles, so a completed search never flashes an empty panel between
// its status flipping and the report body arriving; "missing" only once we KNOW
// the library has nothing, which the search flow turns into a regeneration.
export function resolveReportState({
  drugId,
  libraryReport,
  loading,
  found,
}: ReportStateInput): ReportState {
  if (!drugId) return "idle";
  if (libraryReport) return "ready";
  if (loading || found === null) return "loading";
  return "missing";
}
